"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Menu, X } from "lucide-react";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="201748" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-20 bg-muted animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-mono font-medium text-xs uppercase tracking-widest text-foreground relative py-2 transition-colors hover:text-primary after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-0 after:-translate-x-1/2 after:bg-primary after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
      <nav className="flex flex-col px-4 py-6 gap-4 container mx-auto">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted animate-pulse rounded-sm" />)}
          </div> : menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} onClick={() => setIsOpen(false)} className="font-mono font-medium text-xs uppercase tracking-widest text-foreground py-3 border-b border-border/50 hover:text-primary transition-colors" data-index={i}>
              {item.label}
            </Link>)}
        <Button className="mt-4 bg-primary text-primary-foreground w-full py-6 text-lg font-default font-bold uppercase tracking-tight shadow-md rounded-sm">
          Request a Quote
        </Button>
      </nav>
    </div>;
}
export default function MainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-border shadow-md" : "bg-background border-border/50"}`}>
      <MenuProvider menu_id="18">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {}
            <Link to="#" className="flex-shrink-0 flex items-center">
              <WvcLogo className="h-12 w-auto" />
            </Link>

            {}
            <div className="flex-1 flex justify-center">
              <DesktopNav />
            </div>

            {}
            <div className="hidden md:flex items-center justify-end flex-shrink-0">
              <Button className="bg-primary text-primary-foreground font-default font-bold uppercase tracking-tight px-6 py-5 transition-all duration-300 hover:bg-primary/90 rounded-sm shadow-md hover:shadow-xl hover:ring-2 hover:ring-primary hover:ring-offset-2 hover:-translate-y-0.5">
                Request a Quote
              </Button>
            </div>

            {}
            <div className="md:hidden flex items-center justify-end flex-shrink-0">
              <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:text-primary transition-colors" aria-label="Toggle mobile menu">
                {isMobileMenuOpen ? <X className="size-7" /> : <Menu className="size-7" />}
              </Button>
            </div>
          </div>
        </div>

        {}
        <MobileNav isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </section>;
}